// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { Permission } from './permissions.js';
import { LOGISTICS_EXPORT_GATES, LOGISTICS_EXPORT_KINDS, LOGISTICS_EXPORT_OWNER_TYPES, } from '../types/logistics.js';
import { MANUFACTURING_EXPORT_GATES } from '../types/manufacturing.js';
import { ISSUED_RECORD_EXPORT_GATES, ISSUED_RECORD_EXPORT_OWNER_TYPE, } from '../types/issued-records.js';
import { SCHEDULED_EXPORT_GATES, SCHEDULED_EXPORT_RUN_OWNER_TYPE, } from '../types/export-schedule.js';
/**
 * Sprint 64.92.0 — the owner types whose retrieval grant is resolved from the
 * export's **kind**, not from the owner type alone.
 *
 * ## The defect, which was wrong in both directions from one cause
 *
 * 64.0.0's rule is that retrieval honours the permission that gated creation.
 * One owner type per module breaks it both ways, and both instances were live:
 *
 * - **Broader** (idea-catalog #118). `manufacturing_export` served all eight
 *   manufacturing registers and mapped to `manufacturing_product:read`, so a
 *   holder of that one grant could download an exported substance register or a
 *   remote-access-grant register — material no route in the module would return
 *   them. Seven of the eight registers were retrievable too widely.
 * - **Narrower** (idea-catalog #140). `logistics_export` served all ten logistics
 *   registers and mapped to `logistics_shipment:read`, so a role holding
 *   `logistics_claim:read` and not `logistics_shipment:read` could create a
 *   claims export and was then 403'd retrieving its own file. 62.9.0 closed that
 *   instance by splitting the logistics owner types one-per-grant; the mechanism
 *   that produced it survived, in the module next door.
 *
 * ## Why the fix is not "more owner types"
 *
 * Six more for manufacturing, then nine for the next module, grows a
 * hand-maintained map of 200-odd entries with no derivation, and the policy's own
 * strength (an unmapped type fails closed) never fires, because every one of
 * these **is** mapped — to the wrong thing.
 *
 * The kind is already known at creation, is already a typed union per module, and
 * is already recorded on the artefact by both export services
 * (`metadata: { kind }`). So the grant is resolved from `(owner type, kind)`, and
 * a register whose kind this table does not cover fails closed to the T1 fallback
 * with a diagnostic that **names the kind** — instead of silently inheriting a
 * sibling register's permission.
 */
export const KINDED_EXPORT_OWNER_TYPES = [
    ...LOGISTICS_EXPORT_OWNER_TYPES,
    'manufacturing_export',
    // Sprint 62.18.0 — two per-subject histories under one owner type, both
    // gated on the register's `:read`; kinded so a third history added later
    // fails closed by name rather than inheriting these two's grant.
    ISSUED_RECORD_EXPORT_OWNER_TYPE,
    // Sprint 63.30.0 (#584) — a scheduled export run's file, kinded by the
    // schedule's export type: each kind downloads under the grant its run's actor
    // had to hold to produce it (`SCHEDULED_EXPORT_GATES`).
    SCHEDULED_EXPORT_RUN_OWNER_TYPE,
];
/**
 * The logistics half, **derived** from the table that gates creation.
 *
 * `LOGISTICS_EXPORT_GATES` is the same declaration `logistics/routes.ts` builds
 * its ten export routes from, so a grant changed on one side is changed on both,
 * because there is only one side. The cast is over key *coverage* only — every
 * member of `LOGISTICS_EXPORT_OWNER_TYPES` is the `owner_type` of at least one
 * kind, asserted in both directions by `attachment-download-policy.test.ts`.
 */
const LOGISTICS_KIND_GATES = (() => {
    const byOwnerType = {};
    for (const kind of LOGISTICS_EXPORT_KINDS) {
        const { owner_type, permission } = LOGISTICS_EXPORT_GATES[kind];
        const bucket = byOwnerType[owner_type] ?? {};
        bucket[kind] = permission;
        byOwnerType[owner_type] = bucket;
    }
    return byOwnerType;
})();
/**
 * `(owner type, export kind) → the permission that gated that kind's creation`.
 *
 * Both halves are read from the module's own creation-gate declaration; neither
 * is transcribed here. A kind absent from a module's table is not a permissive
 * hole — `resolveDownloadGrant` fails it closed and names it.
 */
export const EXPORT_KIND_DOWNLOAD_GATES = {
    ...LOGISTICS_KIND_GATES,
    manufacturing_export: MANUFACTURING_EXPORT_GATES,
    [ISSUED_RECORD_EXPORT_OWNER_TYPE]: ISSUED_RECORD_EXPORT_GATES,
    [SCHEDULED_EXPORT_RUN_OWNER_TYPE]: SCHEDULED_EXPORT_GATES,
};
/**
 * The union of a kinded owner type's grants — the row these types keep in
 * `OWNER_TYPE_DOWNLOAD_POLICY`.
 *
 * It is **not** the download decision. It is the coarse "could this viewer
 * retrieve *any* file stored under this type" question, which is the only one an
 * owner type can answer, and it is used for exactly one thing: the first pass of
 * the enumeration filter, which then narrows per row by the recorded kind
 * (`partitionOwnerTypesByPermission` → `kinded[]`). Serving bytes never consults
 * it; `resolveDownloadGrant` reaches the kind table first.
 *
 * For the six logistics types the union is one permission — each of those types
 * already carries exactly one grant — so this reproduces 62.9.0's rows exactly.
 * For `manufacturing_export` it is six over eight registers, which is why the
 * enumeration filter has to narrow and why a test asserts that it does.
 */
const KINDED_EXPORT_UNION_POLICY = Object.fromEntries(KINDED_EXPORT_OWNER_TYPES.map((ownerType) => [
    ownerType,
    [...new Set(Object.values(EXPORT_KIND_DOWNLOAD_GATES[ownerType]))],
]));
/**
 * Sprint 64.0.0 — owner-type-aware attachment download policy.
 *
 * `attachments:download` and `platform:files:detail:read` are flat
 * permissions: before this sprint, holding either meant every file in the
 * org was retrievable, regardless of which permission gated the file's
 * *creation*. The declared auditor holds both plus
 * `framework_evidence_pack:read` — and deliberately not `:create` — so the
 * role explicitly denied the right to mint a filing-grade evidence pack
 * could browse to one and download it (idea-catalog #46, raised by Sprint
 * 62.2.3's gate E12.5 security review).
 *
 * This map declares, per owner type, the permission(s) a caller must hold
 * IN ADDITION to the route's base permission before the platform serves
 * that file's bytes or metadata. Semantics:
 *
 * - `readonly Permission[]` — the caller must hold AT LEAST ONE of the
 *   listed permissions (any-of, mirroring `requireAnyPermission`).
 * - `null` — deliberately no additional requirement. Used only where the
 *   broadest read is the point (`client_csv_export`, whose producer gate is
 *   itself only `attachments:upload`).
 *
 * Authoring rules (decided in sprint-64.0.0-workfile.md, reviewed by the
 * Security and Compliance specialists at gate E12.5):
 *
 * 1. DEFAULT: the union of the type's creating-route permission(s) and its
 *    module's read permission(s). Download protects against readers from
 *    OUTSIDE the module (platform-files browsers, generic
 *    `attachments:download` holders); it does not narrow access within a
 *    module whose pages already gate by module read.
 * 2. ESCALATED types list ONLY their creation/export permission. These are
 *    the artefacts whose producers deliberately gate creation more tightly
 *    than the module read — an identity-bearing dump or filing-grade
 *    archive must not be retrievable on the strength of `:read` alone.
 *    The source comment at `rights-requests/routes.ts` (EXPORT_BULK over
 *    CASE_READ, "identity-bearing dump") is the in-repo precedent.
 * 3. The map is EXPLICIT and total: `Record<AttachmentOwnerType, …>` makes
 *    a new enum value without an entry a compile error, and
 *    `attachment-download-policy.test.ts` walks the enum at runtime so the
 *    omission also fails a suite naming the type.
 * 4. A stored row whose `owner_type` is not a key of this map (legacy or
 *    unknown string in the database) falls back to
 *    `OWNER_TYPE_DOWNLOAD_FALLBACK` — the strictest platform-files
 *    permission, held only by T1 roles. A type nobody has decided on fails
 *    closed until somebody decides (spec 64.0.0, locked decisions).
 */
export const OWNER_TYPE_DOWNLOAD_POLICY = {
    // ── Governance (Sprint 41.0.0 G2-G5 + exports) ─────────────────────
    governance_evidence: [Permission.GOV_EVIDENCE_READ],
    playbook_execution_step: [Permission.GOV_EXECUTION_READ],
    offboarding_data_return: [Permission.GOV_OFFBOARDING_READ],
    approval_decision: [Permission.GOV_APPROVAL_READ],
    governance_cross_border_export: [Permission.GOV_CROSSBORDER_READ, Permission.GOV_EXPORT_PDF],
    governance_board_report_export: [Permission.GOV_EXPORT_PDF],
    governance_evidence_bundle_export: [Permission.GOV_EVIDENCE_READ, Permission.GOV_EXPORT_PDF],
    // ── Vendor Risk ────────────────────────────────────────────────────
    vendor_questionnaire_answer: [Permission.VENDOR_QUESTIONNAIRE_READ],
    vendor_onboarding_case: [Permission.VENDOR_ONBOARDING_CASE_READ],
    vendor_onboarding_export: [
        Permission.VENDOR_ONBOARDING_CASE_READ,
        Permission.VENDOR_ONBOARDING_CASE_EXPORT,
    ],
    vendor_questionnaire_export: [Permission.VENDOR_QUESTIONNAIRE_READ],
    vendor_questionnaire_template_export: [Permission.VENDOR_QUESTIONNAIRE_TEMPLATE_READ],
    vendor_risk_acceptance_export: [Permission.VENDOR_RISK_ACCEPTANCE_READ],
    vendor_subprocessor_register_export: [Permission.VENDOR_ONBOARDING_CASE_READ],
    vendor_contract: [Permission.VENDOR_CONTRACT_READ],
    vendor_soc_report: [Permission.VENDOR_CONTRACT_READ],
    vendor_dpa: [Permission.VENDOR_CONTRACT_READ],
    vendor_nda: [Permission.VENDOR_CONTRACT_READ],
    vendor_termination_notice: [Permission.VENDOR_CONTRACT_READ],
    vendor_amendment: [Permission.VENDOR_CONTRACT_READ],
    // ── Rights Requests ────────────────────────────────────────────────
    // Sprint 64.35.0 — the any-of removed, and what it did and did not change.
    //
    // `case:attach:download` was enforced NOWHERE: the legacy attachment routes
    // it gated went in 41.2.0, and its one remaining site was this row, where
    // the `CASE_READ` alternative made it vacuous. DSR identity-proof material
    // sat under bare `case:read` — the closest sibling of this module's own
    // `EXPORT_BULK`-over-`CASE_READ` "identity-bearing dump" precedent three
    // rows down.
    //
    // Measured across all seven seeded roles, this changes no seeded reach.
    // The route's base permission is `attachments:download`, held only by
    // platform_admin, compliance_owner and auditor — and all three also hold
    // `case:attach:download`. The roles that held `case:read` without it
    // (`approver`, `processor_liaison`) could not call the download route at
    // all, so nothing shipped breaks.
    //
    // What it does change is CUSTOM roles: one carrying
    // `attachments:download` + `case:read` and not `case:attach:download` was
    // admitted here and is not any more. That is the intent.
    //
    // Found while measuring, NOT fixed here (it is a grant question, not a
    // policy one): `case_manager` — "Daily case operations and DSR processing"
    // — holds `case:attach:download` but NOT `attachments:download`, so it
    // cannot retrieve DSR case evidence through any row of this map;
    // `partner_external_counsel`'s identical grant is unreachable for the same
    // reason. Routed rather than widened.
    rights_request_attachment: [Permission.CASE_ATTACH_DOWNLOAD],
    rights_request_case_file: [Permission.CASE_READ],
    rights_request_response_export: [Permission.CASE_READ],
    // ESCALATED (rule 2): creation is deliberately EXPORT_BULK over
    // CASE_READ — "an identity-bearing dump". Retrieval honours it.
    rights_request_evidence_pack: [Permission.EXPORT_BULK],
    rights_request_bulk_export: [Permission.EXPORT_BULK],
    // Legacy stub owner type (superseded by the two above; historical rows
    // only). Same escalation as its successors.
    case_evidence_pack_export: [Permission.EXPORT_BULK],
    // ── Attachment-field evidence types (Sprint 41.3.0 F0) ─────────────
    data_mapping_evidence: [Permission.DM_EVIDENCE_READ],
    deletion_step_execution: [Permission.DELETION_ORCHESTRATION_READ],
    incident_evidence: [Permission.INCIDENT_READ],
    monitoring_task_instance: [Permission.REVIEW_TASK_READ],
    dpia_record: [Permission.DPIA_READ],
    transfer_assessment_record: [Permission.TRANSFER_ASSESSMENT_READ],
    form_instance_response: [Permission.FORMS_INSTANCE_READ],
    consent_workflow: [Permission.CONSENT_WORKFLOW_READ],
    /*
      Sprint 62.28.0 — the held-here copy of a marketing consent. Gated on the
      register's own read grant and nothing wider: the file IS the proof of the
      agreement, so anyone who may read the register may read what it rests on,
      and nobody else. Deliberately NOT the unmask grant, which protects a
      restricted CUSTODY handle (a pointer to someone else's system) and is a
      different question from reading the organisation's own copy.
    */
    marketing_consent_evidence: [Permission.MARKETING_COMMUNICATIONS_READ],
    /*
      Sprint 62.29.0 — evidence behind an AI system's governance entries. The
      register's own read grant, as for marketing consent evidence: whoever may
      read the entry may read what it rests on.
    */
    ai_governance_evidence: [Permission.AI_GOVERNANCE_READ],
    /*
      Sprint 64 (E12.5 security S1) — the payment-authority export. The route is
      gated on AUTHORITY, so the download is too: a READ holder is refused.
    */
    ai_governance_authority_export: [Permission.AI_GOVERNANCE_AUTHORITY],
    marketing_suppression_evidence: [Permission.MARKETING_COMMUNICATIONS_READ],
    incident_ops_evidence: [Permission.INCIDENT_OPS_EVIDENCE_CHAIN_READ],
    /*
      Sprint 62.53.0 (AC4) — evidence behind an organisation-profile statement.
      The grant that reads the statement (`GET /org-settings/profile-statements/
      :attribute` is gated on it), and nothing wider: whoever may read what the
      organisation stated may read what the statement rests on.
    */
    org_profile_statement: [Permission.PLATFORM_SECURITY_READ],
    publication_prerequisite_evidence: [Permission.PUBLICATION_READ],
    /*
      Sprint 62.57.0 (Slice R) — the copy of an authority's communication on a
      filing, and the filing's communication-history export, download under the
      read that shows the filing and its history (`dpia:read`). The upload's
      derived write grant is `dpia:write`, the grant that records the entry (D1).
    */
    dossier_authority_communication: [Permission.DPIA_READ],
    dossier_authority_communication_export: [Permission.DPIA_READ],
    /*
      Sprint 62.58.0 (Slice C) — a document behind a briefing or consultation
      comment on a framework, and a framework summary export, download under
      the read that shows the framework's briefings and produces the summary
      (`regulatory_framework:read`). The upload's derived write grant is
      `regulatory_framework:write`, the grant that records the entry (AC-C3).
    */
    framework_briefing: [Permission.REGULATORY_FRAMEWORK_READ],
    framework_summary_export: [Permission.REGULATORY_FRAMEWORK_READ],
    /*
      Sprint 62.57.0 Slice E (work file D65, D66) — every file of the
      processing-service certificate register (a certificate's documents, an
      application's dossier parts, the copy of an authority's communication)
      and its exports download under the read that opens the register. The
      upload's derived write grant is `service_certificate:write`.
    */
    service_certificate_document: [Permission.SERVICE_CERTIFICATE_READ],
    service_certificate_application_document: [Permission.SERVICE_CERTIFICATE_READ],
    service_certificate_communication: [Permission.SERVICE_CERTIFICATE_READ],
    service_certificate_export: [Permission.SERVICE_CERTIFICATE_READ],
    // K2, coordinator ruling (e): a return's files and a supplement's files.
    service_certificate_return: [Permission.SERVICE_CERTIFICATE_READ],
    service_certificate_supplement_sent: [Permission.SERVICE_CERTIFICATE_READ],
    /*
      Sprint 62.57.0 Slice E (§12.13, D81) — evidence on the controller-side
      vendor certificate record downloads under the vendor register's read.
      The upload's derived write grant is `gov:vendor:write`.
    */
    vendor_service_certificate_evidence: [Permission.GOV_VENDOR_READ],
    /*
      Sprint 62.58.0 (Slice N, AC-N3) — a document behind a revision of a
      processing activity's access statement downloads under the read that
      shows the statement (`dm:activity:read`). The upload's derived write
      grant is `dm:activity:write`, the grant that records a revision.
    */
    access_statement: [Permission.DM_ACTIVITY_READ],
    // ── Framework evidence packs (Sprint 62.2.x) ───────────────────────
    // ESCALATED (rule 2) — THE defect this sprint exists for. The pack is a
    // filing-grade archive; `framework_evidence_pack:read` (which the
    // auditor holds) previews what a pack would contain, and `:create`
    // (which the auditor deliberately lacks) mints one. Retrieval of the
    // minted bytes follows `:create`.
    framework_evidence_pack_export: [Permission.FRAMEWORK_EVIDENCE_PACK_CREATE],
    // Deliberately broader than the export above (rule 1): the hard-copy
    // proof is human-supplied evidence ABOUT a dispatch (a courier slip),
    // not the filing artefact itself; pack readers may inspect it.
    framework_evidence_pack_hard_copy_proof: [Permission.FRAMEWORK_EVIDENCE_PACK_READ],
    // ── PDPL evidence packs (Sprints 58.x) ─────────────────────────────
    // ESCALATED (rule 2) — Sprint 64.35.0, idea-catalog #88.
    //
    // These seven rows read `[Permission.DPIA_READ]` from 64.0.0 until now,
    // and 64.0.0 said why in a comment rather than routing it: the four PDPL
    // surfaces (SendRecordDrawer, AuthorityResponsePanel,
    // HardCopyFollowupPanel, EmailDraftPage) shipped reader-population
    // download flows gated on DPIA_READ, so mapping straight to the creation
    // permission would have broken shipped, currently-permitted downloads
    // (that sprint's AC8). The exposure it left open is the one 64.0.0 closed
    // for the framework pack directly above: **a role denied the right to mint
    // a filing-grade archive could still download one.** The seeded auditor
    // holds DPIA_READ through the `:read` suffix filter.
    //
    // 64.35.0 does the migration in the order the AC8 note asked for — the
    // grant lands, then the rows flip — with `pdpl_evidence_pack:read` as the
    // retrieval sibling of `pdpl_evidence_pack:send`.
    //
    // Measured populations (`seed-roles.ts`, all seven seeded roles):
    //
    //   before (DPIA_READ ∩ attachments:download) = platform_admin,
    //     compliance_owner, auditor
    //   after  (PDPL_EVIDENCE_PACK_READ ∩ attachments:download) =
    //     platform_admin, compliance_owner
    //
    // The auditor is the whole delta and is the point (AC1). `case_manager`
    // is unaffected in both directions: it holds neither DPIA_READ nor
    // `attachments:download`, so it never reached these rows.
    //
    // Note `pdpl_evidence_pack:read` ends in `:read` and is therefore excluded
    // by NAME from `reachesAuditorBySuffix` — see that function. Without the
    // exception the flip below would have been a no-op dressed as a fix.
    pdpl_evidence_pack_dpia_export: [Permission.PDPL_EVIDENCE_PACK_READ],
    pdpl_evidence_pack_otia_export: [Permission.PDPL_EVIDENCE_PACK_READ],
    pdpl_email_send_proof_2026: [Permission.PDPL_EVIDENCE_PACK_READ],
    pdpl_authority_response_evidence_2026: [Permission.PDPL_EVIDENCE_PACK_READ],
    pdpl_hard_copy_receipt_2026: [Permission.PDPL_EVIDENCE_PACK_READ],
    cover_letter_dpia_dossier: [Permission.PDPL_EVIDENCE_PACK_READ],
    cover_letter_mps_submission: [Permission.PDPL_EVIDENCE_PACK_READ],
    // ── Forseti / AI artefacts ─────────────────────────────────────────
    // One owner type, four producing surfaces (chat conflict export, chat
    // transcript, tabular review XLSX, skill-generated DOCX/redline) —
    // any-of across those surfaces' permissions.
    forseti_artifact: [
        Permission.AI_ADVISOR_CHAT,
        Permission.AI_ADVISOR_CHAT_EXPORT,
        Permission.AI_TABULAR_REVIEWS_READ,
    ],
    // ── Incidents ──────────────────────────────────────────────────────
    incident_report_export: [Permission.INCIDENT_READ, Permission.INCIDENT_EXPORT_EXECUTE],
    incident_reports_list_export: [Permission.INCIDENT_READ, Permission.INCIDENT_EXPORT_EXECUTE],
    // ── Data mapping + classification exports ──────────────────────────
    // Six producers under three creation permissions share this owner type
    // (measured); the union keeps every producing population able to
    // retrieve what it produced.
    data_mapping_export: [
        Permission.DM_EXPORT_CSV,
        Permission.DM_EXPORT_PDF,
        Permission.DM_REGISTRY_EXPORT,
        Permission.DM_SYSTEM_READ,
    ],
    classification_export: [Permission.CLASSIFICATION_READ],
    // Sprint 62.13.0 — downloading an exposure report or a case-register export
    // needs the same read the producing screens require.
    penalty_exposure_export: [Permission.PENALTY_EXPOSURE_READ],
    enforcement_case_export: [Permission.ENFORCEMENT_CASE_READ],
    // Sprint 62.14.0 — each register export downloads under its own :read.
    authority_direction_export: [Permission.AUTHORITY_DIRECTION_READ],
    legal_hold_export: [Permission.LEGAL_HOLD_READ],
    regulated_account_export: [Permission.REGULATED_ACCOUNT_READ],
    // Sprint 62.24.0 — the two register exports download under their own :read.
    credit_information_supply_export: [Permission.CREDIT_INFORMATION_SUPPLY_READ],
    data_service_provider_export: [Permission.DATA_SERVICE_PROVIDER_READ],
    // Sprint 62.45.0 — the period and inspection evidence packs download under
    // the read that opens the register they reproduce.
    supervisory_reporting_export: [Permission.PAYMENTS_EVIDENCE_READ],
    // Sprint 62.45.0 Slice H (AC23) — a file a register record rests on (a
    // signed report, a receipt, a notice email) downloads under the read that
    // opens the record.
    supervisory_reporting_evidence: [Permission.PAYMENTS_EVIDENCE_READ],
    // Sprint 63.24.0 Slice B — the qualification evidence a personnel record's
    // role rests on downloads under the read that opens the case it belongs to.
    controlled_testing_personnel_evidence: [Permission.CONTROLLED_TESTING_CASE_READ],
    // Sprint 63.24.0 Slice C — the technical statement of a solution record downloads
    // under the read that opens the case it belongs to.
    controlled_testing_solution_evidence: [Permission.CONTROLLED_TESTING_CASE_READ],
    controlled_testing_child_results_report: [Permission.CONTROLLED_TESTING_CASE_READ],
    // Sprint 63.24.0 Slice D — the supplement file of a registry row downloads under the case read.
    controlled_testing_registry_evidence: [Permission.CONTROLLED_TESTING_CASE_READ],
    // Sprint 63.24.0 Slice F — the stop, revocation and closure files download under the case read.
    controlled_testing_stop_evidence: [Permission.CONTROLLED_TESTING_CASE_READ],
    // Sprint 63.24.0 Slice F — the completion files, the authority's copy included, under the case read.
    controlled_testing_completion_evidence: [Permission.CONTROLLED_TESTING_CASE_READ],
    // Sprint 62.50.0 — a file a supervision-information record rests on downloads
    // under the read that opens the register.
    pcf_supervision_evidence: [Permission.PCF_SUPERVISION_READ],
    // Sprint 62.55.0 — a file a customs tax-administration record rests on downloads
    // under the read that opens the registers.
    customs_tax_administration_evidence: [Permission.CUSTOMS_TAX_ADMINISTRATION_READ],
    // Sprint 62.55.0 (AC-F2) — the register exports download under the read that
    // shows the same rows on screen; `:export` is the grant that produces them.
    customs_tax_administration_export: [Permission.CUSTOMS_TAX_ADMINISTRATION_READ],
    // Sprint 62.56.0 — a file a capture component's evidence area or a custody
    // transfer rests on downloads under the read that opens the register. The
    // inspection evidence pack downloads under the grant that produces it
    // (gate E12.5 pass 1, SC-P3): a pack reproduces every register the reader
    // may not otherwise have asked to see together — the AML export precedent.
    biometric_capture_evidence: [Permission.BIOMETRIC_CAPTURE_READ],
    biometric_capture_export: [Permission.BIOMETRIC_CAPTURE_EXPORT],
    // Sprint 62.50.1 — a register export downloads under the read that opens the register.
    pcf_supervision_export: [Permission.PCF_SUPERVISION_READ],
    // Sprint 62.51.0 — a file a data-exploitation record rests on, and a register
    // or grant export, download under the read that opens the registers.
    data_exploitation_evidence: [Permission.DATA_EXPLOITATION_READ],
    data_exploitation_export: [Permission.DATA_EXPLOITATION_READ],
    // Sprint 62.52.0 (owner answer OQ-2 (a)) — a period's evidence and its export
    // are read by the period's MEMBERS through the module's membership-checked
    // route; the generic route serves them only to a holder of the restricted
    // read, who may read every period. `:read` alone never reaches them.
    cybersecurity_assurance_index_evidence: [
        Permission.CYBERSECURITY_ASSURANCE_INDEX_RESTRICTED_READ,
    ],
    cybersecurity_assurance_index_export: [
        Permission.CYBERSECURITY_ASSURANCE_INDEX_RESTRICTED_READ,
    ],
    // Sprint 62.49.0 — a file a food traceability record rests on, and a register
    // export or a recall case's internal report copy, download under the read
    // that opens the registers. The upload grant (`food_traceability:write`)
    // follows from the read by verb substitution; the export is system-generated.
    food_traceability_evidence: [Permission.FOOD_TRACEABILITY_READ],
    food_traceability_export: [Permission.FOOD_TRACEABILITY_READ],
    // Sprint 65.1.0 — the work-programme / completeness package downloads under
    // the read that gates the programme it reproduces. `:export` is the grant
    // that produces it; `:read` is what a holder needs to see the same rows on
    // screen, and 64.0.0's rule is that retrieval honours the creation grant —
    // here they are the same population, because `:export` is granted only
    // alongside `:read` in every seeded role.
    compliance_obligation_export: [Permission.COMPLIANCE_OBLIGATION_READ],
    // ── Audit ──────────────────────────────────────────────────────────
    audit_events_export: [Permission.AUDIT_EVENTS_READ, Permission.AUDIT_EVENTS_EXPORT],
    // Sprint 63.25.0 (spec F3) — a submission proof downloads under the grant
    // that records the submission (64.0.0: retrieval honours the creation
    // grant). `:export` is not `:read`-suffixed, so no write analogue is
    // derived and the upload floor is this same grant.
    audit_evidence_pack_submission: [Permission.AUDIT_EVENTS_EXPORT],
    // ── Consent ────────────────────────────────────────────────────────
    consent_audit_export: [Permission.CONSENT_POLICY_READ],
    consent_evidence_pack_export: [Permission.CONSENT_CROSS_PROPERTY_EXPORT],
    consent_property_binding_export: [Permission.CONSENT_BINDING_READ],
    consent_property_registry_export: [Permission.CONSENT_PROPERTY_READ],
    consent_property_change_requests_export: [Permission.CONSENT_CHANGE_REQUEST_READ],
    // ── Discovery ──────────────────────────────────────────────────────
    discovery_report_export: [Permission.DISCOVERY_READ],
    discovery_classification_rules_export: [Permission.CLASSIFICATION_RULE_READ],
    discovery_dedup_rules_export: [Permission.DISCOVERY_READ],
    discovery_import_templates_export: [Permission.DISCOVERY_READ],
    discovery_connectors_export: [Permission.DISCOVERY_READ],
    discovery_scans_export: [Permission.DISCOVERY_READ],
    discovery_staging_export: [Permission.DISCOVERY_READ],
    discovery_suggestions_export: [Permission.CLASSIFICATION_SUGGESTION_READ],
    discovery_analytics_export: [Permission.DISCOVERY_READ],
    // ── Connectors ─────────────────────────────────────────────────────
    connector_automation_programs_export: [Permission.CONNECTOR_READ],
    connector_system_inventory_export: [Permission.CONNECTOR_READ],
    connector_traceability_matrix_export: [Permission.CONNECTOR_READ],
    connector_gap_register_export: [Permission.CONNECTOR_READ],
    connector_brds_export: [Permission.CONNECTOR_READ],
    connector_decision_records_export: [Permission.CONNECTOR_READ],
    connector_control_mappings_export: [Permission.CONNECTOR_READ],
    connector_instances_export: [Permission.CONNECTOR_READ],
    connector_definitions_export: [Permission.CONNECTOR_READ],
    connector_poc_scorecards_export: [Permission.CONNECTOR_READ],
    connector_security_reviews_export: [Permission.CONNECTOR_READ],
    // ESCALATED (rule 2): the event-bus and credentials LIST routes require
    // EVENT_BUS_READ / CREDENTIAL_READ, stricter than the CONNECTOR_READ
    // their shared export route demands (a live creation-side downgrade,
    // idea-catalog #85 / Sprint 64.32.0). Retrieval honours the list gate —
    // a principal who cannot list credential metadata cannot download the
    // exported file of it either.
    connector_event_bus_export: [Permission.EVENT_BUS_READ],
    connector_credentials_export: [Permission.CREDENTIAL_READ],
    // ── Deletion orchestration ─────────────────────────────────────────
    deletion_execution_tasks_export: [Permission.DELETION_ORCHESTRATION_READ],
    deletion_vendor_requests_export: [Permission.DELETION_VENDOR_REQUEST_READ],
    deletion_proof_chain_export: [Permission.DELETION_PROOF_READ],
    // ── Incident operations ────────────────────────────────────────────
    incident_ops_playbook_templates_export: [Permission.INCIDENT_OPS_PLAYBOOK_LINK_READ],
    incident_ops_regulator_exports_export: [Permission.INCIDENT_OPS_COORDINATION_READ],
    incident_ops_response_metrics_export: [Permission.INCIDENT_OPS_REGULATORY_SLA_READ],
    incident_ops_sla_dashboard_export: [Permission.INCIDENT_OPS_REGULATORY_SLA_READ],
    incident_ops_sla_definitions_export: [Permission.INCIDENT_OPS_REGULATORY_SLA_READ],
    incident_ops_vendor_coordination_export: [Permission.INCIDENT_OPS_COORDINATION_READ],
    // ── Access accountability ──────────────────────────────────────────
    access_accountability_export: [Permission.ACCESS_LOG_READ],
    access_session_events_export: [Permission.ACCESS_SESSION_READ],
    access_anchoring_proofs_export: [Permission.ACCESS_LOG_READ],
    access_justifications_export: [Permission.ACCESS_JUSTIFICATION_READ],
    access_investigations_list_export: [Permission.ACCESS_INVESTIGATION_READ],
    access_policies_export: [Permission.ACCESS_CONFIG_READ],
    access_review_campaigns_export: [Permission.ACCESS_CONFIG_READ],
    access_evidence_packages_export: [Permission.ACCESS_LOG_READ],
    // ESCALATED (rule 2): their creating routes gate on the EXPORT
    // permissions, deliberately narrower than the family's read — the
    // investigation case file and the assembled evidence package are the
    // most sensitive artefacts in this family.
    access_investigation_case_export: [Permission.ACCESS_INVESTIGATION_EXPORT],
    access_evidence_package_detail_export: [Permission.ACCESS_LOG_EXPORT],
    access_log_list_export: [Permission.ACCESS_LOG_EXPORT],
    // ── Organizations (cross-tenant reach) ─────────────────────────────
    // Creation additionally requires the platform_admin role (the only
    // stacked role+permission gate in the codebase); the map can express
    // permissions only, so the read gate is the org-manage read.
    organizations_export: [Permission.ORG_MANAGE_READ],
    organization_intake_configs_export: [Permission.INTAKE_CONFIG_READ],
    organization_departments_export: [Permission.ORG_MANAGE_READ],
    organization_framework_assignments_export: [Permission.ORG_MANAGE_READ],
    organization_compliance_rollup_export: [Permission.ORG_MANAGE_READ],
    // ── Org groups ─────────────────────────────────────────────────────
    org_groups_list_export: [Permission.GROUP_READ],
    org_group_dashboard_export: [Permission.GROUP_DASHBOARD_READ],
    org_group_reports_export: [Permission.GROUP_REPORTING_READ],
    org_group_audit_export: [Permission.GROUP_AUDIT_READ],
    org_group_consolidated_audit_export: [Permission.GROUP_AUDIT_READ],
    org_group_per_org_audit_export: [Permission.GROUP_AUDIT_READ],
    org_group_users_export: [Permission.GROUP_READ],
    org_group_shared_vendors_export: [Permission.GROUP_SHARED_DATA_READ],
    org_group_shared_frameworks_export: [Permission.GROUP_SHARED_DATA_READ],
    org_group_shared_form_templates_export: [Permission.GROUP_SHARED_DATA_READ],
    org_group_merged_vendors_export: [Permission.GROUP_SHARED_DATA_READ],
    // ── Partner workspace ──────────────────────────────────────────────
    partner_orgs_export: [Permission.PARTNER_ORG_READ],
    partner_org_detail_export: [Permission.PARTNER_ORG_READ],
    partner_dashboard_export: [Permission.PARTNER_ORG_READ],
    partner_reports_export: [Permission.PARTNER_REPORT_READ],
    partner_audit_list_export: [Permission.PARTNER_AUDIT_READ],
    partner_workflow_templates_export: [Permission.PARTNER_WORKFLOW_READ],
    partner_workflow_instances_export: [Permission.PARTNER_WORKFLOW_READ],
    partner_deliverables_export: [Permission.DELIVERABLE_READ],
    partner_deliverable_templates_export: [Permission.DELIVERABLE_READ],
    partner_support_tickets_export: [Permission.PARTNER_TICKET_READ],
    partner_ticket_detail_export: [Permission.PARTNER_TICKET_READ],
    // Rule 1 (default): union of the creating permission (PARTNER_TICKET_WRITE,
    // which gates `POST /support-tickets/:id/attachments`) and the module read.
    // Not escalated — a ticket attachment is working evidence shared between
    // partner and tenant teams, not an identity-bearing dump, and anyone who
    // can read the ticket can already read the note timeline beside it.
    partner_ticket_attachment: [Permission.PARTNER_TICKET_READ, Permission.PARTNER_TICKET_WRITE],
    partner_escalation_dashboard_export: [Permission.PARTNER_TICKET_READ],
    partner_service_tiers_export: [Permission.SERVICE_TIER_READ],
    partner_brand_profiles_export: [Permission.PARTNER_BRAND_READ],
    // ── Task management + workflows (any-of, mirroring the creating
    //    routes' requireAnyPermission) ──────────────────────────────────
    task_list_export: [Permission.TM_EXPORT, Permission.TM_TASK_READ],
    task_board_export: [Permission.TM_EXPORT, Permission.TM_TASK_READ],
    task_detail_export: [Permission.TM_EXPORT, Permission.TM_TASK_READ],
    my_tasks_export: [Permission.TM_EXPORT, Permission.TM_TASK_READ],
    overdue_tasks_export: [Permission.TM_EXPORT, Permission.TM_TASK_READ],
    task_calendar_export: [Permission.TM_EXPORT, Permission.TM_CALENDAR_READ],
    workflow_list_export: [Permission.TM_EXPORT, Permission.WF_DEFINITION_READ],
    workflow_detail_export: [Permission.TM_EXPORT, Permission.WF_DEFINITION_READ],
    workflow_template_catalog_export: [Permission.TM_EXPORT, Permission.WF_TEMPLATE_READ],
    task_conflict_detection_export: [Permission.TM_EXPORT, Permission.TM_TASK_READ],
    task_consolidation_export: [Permission.TM_EXPORT, Permission.TM_TASK_READ],
    task_section_assignments_export: [Permission.TM_EXPORT, Permission.TM_TASK_READ],
    // ── Program governance ─────────────────────────────────────────────
    governance_model_export: [Permission.MATURITY_MODEL_READ],
    audit_readiness_export: [Permission.AUDIT_READINESS_READ],
    document_change_log_export: [Permission.CHANGE_LOG_READ],
    governance_documents_export: [Permission.GOV_DOC_READ, Permission.AI_ADVISOR_CHAT],
    doc_drafting_export: [Permission.GOV_DOC_READ],
    document_issuance_export: [Permission.GOV_DOC_READ],
    maturity_models_export: [Permission.MATURITY_MODEL_READ],
    maturity_walkthrough_export: [Permission.MATURITY_ASSESSMENT_READ, Permission.AI_ADVISOR_CHAT],
    audit_walkthrough_export: [Permission.AUDIT_EVENTS_READ, Permission.AI_ADVISOR_CHAT],
    remediation_plans_export: [Permission.AUDIT_READINESS_READ, Permission.AI_ADVISOR_CHAT],
    reporting_periods_export: [Permission.EXECUTIVE_REPORT_READ],
    trend_metrics_export: [Permission.TREND_DASHBOARD_READ],
    acknowledgment_export: [Permission.ACKNOWLEDGMENT_READ],
    program_health_export: [
        Permission.MATURITY_MODEL_READ,
        Permission.AUDIT_READINESS_READ,
        Permission.TREND_DASHBOARD_READ,
    ],
    raci_matrix_export: [Permission.OBLIGATION_MAPPING_READ, Permission.MONITORING_RACI_READ],
    // ── Monitoring ─────────────────────────────────────────────────────
    monitoring_authority_readiness_export: [Permission.MONITORING_AUTHORITY_READINESS_READ],
    monitoring_change_reviews_export: [Permission.MONITORING_CHANGE_REVIEW_READ],
    monitoring_framework_links_export: [Permission.MONITORING_FRAMEWORK_LINK_READ],
    monitoring_kpi_export: [Permission.KPI_READ],
    monitoring_periods_export: [Permission.PROGRAM_READ],
    monitoring_programs_export: [Permission.PROGRAM_READ],
    monitoring_ric_cases_export: [Permission.RIC_READ],
    monitoring_task_templates_export: [Permission.TASK_TEMPLATE_READ],
    monitoring_tasks_export: [Permission.REVIEW_TASK_READ],
    // ── Forms ──────────────────────────────────────────────────────────
    form_output_dpia: [Permission.FORMS_INSTANCE_READ],
    form_output_transfer_assessment: [Permission.FORMS_INSTANCE_READ],
    form_output_dossier: [Permission.FORMS_INSTANCE_READ, Permission.DPIA_READ],
    form_output_incident_report: [Permission.FORMS_INSTANCE_READ],
    form_output_breach_notification: [Permission.FORMS_INSTANCE_READ],
    form_output_authority_request: [Permission.FORMS_INSTANCE_READ],
    form_output_cross_border_filing: [Permission.FORMS_INSTANCE_READ],
    form_output_other: [Permission.FORMS_INSTANCE_READ],
    // Sprint 64.147.0 — the tier reaches the rendered output. Custody, not
    // `forms:instance:read`: the document reproduces a restricted register's
    // structure, and the eight entries above are held by roles that hold no
    // grant over the register itself.
    form_output_restricted_register: [Permission.REGULATORY_ARTIFACT_CUSTODY],
    form_output_print_export: [Permission.FORMS_INSTANCE_READ],
    form_filing_case_links_export: [Permission.FORMS_INSTANCE_READ],
    // Sprint 63.5.0 (#227) — a supporting document attached to a submission
    // package. `forms:instance:read` is the grant the package itself reads
    // under (`GET /submission-packages/:packageId`), so the attachment and the
    // record it belongs to are retrievable by exactly the same caller.
    submission_package_document: [Permission.FORMS_INSTANCE_READ],
    // ── Cross-cutting ──────────────────────────────────────────────────
    // Deliberately null (rule: broadest read is the point). Produced by any
    // ATTACHMENTS_UPLOAD holder from rows the page already displayed; there
    // is no stronger creation gate to honour. Content validation of the
    // self-declared source_module is idea-catalog #85 / Sprint 64.32.0.
    client_csv_export: null,
    regulatory_artifacts_export: [Permission.REGULATORY_ARTIFACT_READ],
    // ── Sprint 62.7.0 — public data, payments and platform evidence ────
    //
    // A FOURTH structure keyed on `AttachmentOwnerType`. 62.7.0 registered its
    // nine owner types in the three it knew about — the enum, the upload
    // allowlist and the collections map — and this one, added by 64.0.0, caught
    // the omission at the merge through `Record` exhaustiveness. Which is the
    // argument for the `Record`: the two structures guarded only by a drift
    // test would have needed somebody to remember they existed.
    //
    // Everything here is the module read, EXCEPT the payments export. That one
    // is escalated: `payments_evidence:export` carries protected references
    // that are redacted per viewer, and a `:read` holder retrieving the stored
    // artefact later would read whatever the exporter could see at the time.
    // The escalated precedent is the identity-bearing dump at
    // `rights-requests/routes.ts`, and this is the same shape.
    public_data_sharing_agreement: [Permission.PUBLIC_DATA_READ],
    public_data_acknowledgement: [Permission.PUBLIC_DATA_READ],
    ekyc_disposal_evidence: [Permission.PAYMENTS_EVIDENCE_READ],
    payment_data_security_evidence: [Permission.PAYMENTS_EVIDENCE_READ],
    platform_seller_registration_evidence: [Permission.PLATFORM_EVIDENCE_READ],
    platform_document_evidence: [Permission.PLATFORM_EVIDENCE_READ],
    public_data_export: [Permission.PUBLIC_DATA_READ],
    payments_evidence_export: [Permission.PAYMENTS_EVIDENCE_UNMASK],
    platform_evidence_export: [Permission.PLATFORM_EVIDENCE_READ],
    // ── Sprint 62.46.0 — the AML programme register ────────────────────
    //
    // Both follow the case register's read grant, which is custom-only and
    // held by no seeded role. The rule is 64.0.0's (retrieval honours the
    // permission that gated creation) with a sharper reason: a case evidence
    // file or an inspection pack names a case, and a case's existence must not
    // reach a reader nobody named. Mapping either to `payments_evidence:read`
    // would tip off every holder of the broader grant.
    aml_case_evidence: [Permission.AML_CASE_READ],
    aml_programme_export: [Permission.AML_CASE_READ],
    // ── Sprint 62.54.0 — fee lines and application decisions ────────────
    //
    // Each follows the read grant of the register whose row owns the file:
    // a licence fee receipt and a decision document the Sector Licences
    // register (`sector_licence:read`), a sender-name charge receipt the
    // Marketing Communications register. Kept apart so a reader of one
    // register never retrieves the other's receipts.
    //
    // Sprint 62.55.0 (UC-VN-109 F-2) — a receipt also follows
    // `regulatory_fee_line:read`, the cross-register read of the lines
    // themselves (any-of), so the finance function retrieves the receipts of
    // the lines it records without either register's read. The derived write
    // grant follows: `sector_licence:manage` or `marketing_communications:manage`
    // as before, or `regulatory_fee_line:write`. The decision document is NOT
    // widened: it is the outcome, recorded and retrieved on the register.
    licence_fee_receipt: [Permission.SECTOR_LICENCE_READ, Permission.REGULATORY_FEE_LINE_READ],
    sender_name_fee_receipt: [
        Permission.MARKETING_COMMUNICATIONS_READ,
        Permission.REGULATORY_FEE_LINE_READ,
    ],
    licence_fee_decision_document: [Permission.SECTOR_LICENCE_READ],
    // Sprint 62.55.0 (F-1, #684) — the fee-line exports download under the read
    // that produces them; only `FeeLineExportService` writes the type.
    regulatory_fee_line_export: [Permission.REGULATORY_FEE_LINE_READ],
    // ── Sprint 62.8.0 — manufacturing ───────────────────────────
    //
    // 64.0.0's rule: retrieval honours the permission that gated creation. Each of
    // the six follows the module read permission of the register whose rows carry
    // the ids, so whoever may read a conformity file may retrieve its certificate
    // and nobody else. The three are deliberately NOT collapsed onto one grant:
    // recycling evidence, a filing proof and a recording of a vendor session inside
    // plant machinery are read by different people.
    manufacturing_trace_evidence: [Permission.MANUFACTURING_PRODUCT_READ],
    manufacturing_conformity_evidence: [Permission.MANUFACTURING_CONFORMITY_READ],
    manufacturing_epr_fulfilment_evidence: [Permission.MANUFACTURING_EPR_READ],
    manufacturing_epr_submission_proof: [Permission.MANUFACTURING_EPR_READ],
    manufacturing_composition_evidence: [Permission.MANUFACTURING_SUBSTANCE_READ],
    manufacturing_remote_access_evidence: [Permission.MANUFACTURING_OT_READ],
    /*
     * `manufacturing_export` is **not written out here**. Sprint 64.92.0 moved it
     * into `KINDED_EXPORT_UNION_POLICY`, spread in at the end of this map,
     * because one owner type over eight registers cannot state a download
     * decision: it mapped to `manufacturing_product:read` and made seven of the
     * eight registers retrievable by a population that could not produce them.
     * The decision is now `(owner type, kind)` — see `EXPORT_KIND_DOWNLOAD_GATES`.
     */
    // Sprint 62.9.0 — the same rule, and one deliberate asymmetry.
    //
    // Six operator-uploaded evidence types follow the module read permission of
    // the register whose rows carry the ids. `logistics_delivery_proof` follows
    // its **own** grant rather than `logistics_parcel:read`, because the bytes
    // behind it are a signature image or a photograph of a named person's
    // doorstep — the one place in this module where retrieval is a privacy event
    // rather than a paperwork one. That separation is the whole reason the
    // permission exists, and collapsing it here would undo it while leaving the
    // permission looking meaningful.
    logistics_shipment_document: [Permission.LOGISTICS_SHIPMENT_READ],
    logistics_customs_evidence: [Permission.LOGISTICS_CUSTOMS_READ],
    logistics_clearance_evidence: [Permission.LOGISTICS_CUSTOMS_READ],
    logistics_delivery_proof: [Permission.LOGISTICS_DELIVERY_PROOF_READ],
    /**
     * Parcel-timeline evidence follows the **parcel** grant, not a narrower one.
     *
     * Gate E12.5 pass 2. A parcel event is created and read under
     * `logistics_parcel:read`, and 64.0.0's rule is that retrieval honours the
     * grant that gated creation — so gating the bytes more strictly than the row
     * they hang on reproduces exactly the mismatch that rule exists to close,
     * inverted. The argument that an event attachment *might* be a photograph
     * does not separate it from `detail` and `location`, which this grant already
     * returns; the delivery-proof register is separate because a signature
     * belonging to a named person at a known address is a different register with
     * its own permission, not because someone guessed at file contents.
     */
    logistics_parcel_event_evidence: [Permission.LOGISTICS_PARCEL_READ],
    logistics_claim_evidence: [Permission.LOGISTICS_CLAIM_READ],
    logistics_authorisation_evidence: [Permission.LOGISTICS_AUTHORISATION_READ],
    /**
     * The seven kinded export owner types — the six logistics ones
     * (`logistics_export`, `logistics_customs_export`, `logistics_parcel_export`,
     * `logistics_delivery_proof_export`, `logistics_claim_export`,
     * `logistics_authorisation_export`) and `manufacturing_export` — are **not
     * written out here**. They are spread in from `KINDED_EXPORT_UNION_POLICY`
     * above, computed from the same per-module creation-gate tables the export
     * routes are built from.
     *
     * For these seven this row is the coarse enumeration floor, not the download
     * decision: `resolveDownloadGrant` reaches `EXPORT_KIND_DOWNLOAD_GATES` first
     * and answers from the kind recorded on the artefact.
     */
    ...KINDED_EXPORT_UNION_POLICY,
};
/**
 * Fallback for stored rows whose `owner_type` is not a key of the map —
 * a legacy or unknown string in the database. The strictest
 * platform-files permission, held only by T1 roles via allPermissions:
 * an undecided type fails closed until somebody decides.
 */
export const OWNER_TYPE_DOWNLOAD_FALLBACK = Permission.PLATFORM_FILES_HARD_DELETE;
/**
 * Sprint 62.58.0 (gate E12.5 pass 3, P2-1) — the sections an export prints
 * under a grant BEYOND the export's own, and the grants (all-of) each was
 * printed under.
 *
 * ## The defect
 *
 * The RoPA / activities export is built for its creator: the notice coverage
 * is printed under `privacy_notice:read`, the access statements (free-text
 * roles, means and conditions) under `dm:activity:read`, their systems by name
 * under `dm:system:read` as well, and — since Sprint 62.56.0 — the linked
 * biometric capture components under `biometric_capture:read` and the
 * activity's own read. The file was stored as `data_mapping_export`, whose
 * owner-type row is the any-of union of the module's export grants and
 * `dm:system:read`. So a reader of the system inventory could list the file in
 * Platform › Files and download it, reading what no screen would show them.
 *
 * ## Why the kind, and not a new owner type
 *
 * Sprint 64.92.0 settled that the retrieval grant follows what the artefact
 * records about how it was made, and every retrieval path already threads the
 * recorded kind (`recordedExportKind`) to `resolveDownloadGrant`. The producer
 * therefore records, in the kind, which restricted sections it printed
 * (`sectionedExportKind`), and the resolution adds those sections' grants as
 * an all-of. A kind that names no section — every file printed without one,
 * and every file written before this pass — keeps the owner type's rule
 * unchanged. A kind naming a section this table does not declare fails closed
 * to the T1 fallback, named.
 *
 * Grants, not vocabulary (R-46): the keys name sections of the platform's own
 * registers; nothing here names an instrument.
 */
export const RESTRICTED_EXPORT_SECTION_GATES = {
    notice_coverage: [Permission.PRIVACY_NOTICE_READ],
    access_statements: [Permission.DM_ACTIVITY_READ],
    access_statement_system_names: [Permission.DM_ACTIVITY_READ, Permission.DM_SYSTEM_READ],
    biometric_capture_components: [Permission.BIOMETRIC_CAPTURE_READ, Permission.DM_ACTIVITY_READ],
};
const RESTRICTED_EXPORT_SECTION_ORDER = Object.keys(RESTRICTED_EXPORT_SECTION_GATES);
/**
 * The owner types whose recorded kind may name restricted sections. Only
 * these read the sections; any other type's kind means what it meant before.
 */
export const SECTIONED_EXPORT_OWNER_TYPES = new Set([
    'data_mapping_export',
]);
/** Separates the base kind from each section it names: `ropa+notice_coverage`. */
export const EXPORT_SECTION_SEPARATOR = '+';
/**
 * The kind a producer records for an export of `base` that printed
 * `sections`: the base alone when it printed none, otherwise the base and each
 * section in declaration order, deduplicated, so one content has one kind.
 */
export function sectionedExportKind(base, sections) {
    const printed = new Set(sections);
    const ordered = RESTRICTED_EXPORT_SECTION_ORDER.filter((s) => printed.has(s));
    return [base, ...ordered].join(EXPORT_SECTION_SEPARATOR);
}
/**
 * The sections a recorded kind names: `declared` in the table, `undeclared`
 * otherwise. A kind with no separator names none.
 */
export function restrictedSectionsOfKind(exportKind) {
    if (exportKind === null || !exportKind.includes(EXPORT_SECTION_SEPARATOR)) {
        return { declared: [], undeclared: [] };
    }
    const declared = [];
    const undeclared = [];
    for (const token of exportKind.split(EXPORT_SECTION_SEPARATOR).slice(1)) {
        if (Object.prototype.hasOwnProperty.call(RESTRICTED_EXPORT_SECTION_GATES, token)) {
            declared.push(token);
        }
        else {
            undeclared.push(token);
        }
    }
    return { declared, undeclared };
}
/** The restricted sections whose every grant `permissions` holds. */
export function permittedRestrictedSections(permissions) {
    const held = new Set(permissions ?? []);
    return RESTRICTED_EXPORT_SECTION_ORDER.filter((s) => RESTRICTED_EXPORT_SECTION_GATES[s].every((p) => held.has(p)));
}
/**
 * The export kind recorded on an artefact, read from the attachment's
 * `metadata`.
 *
 * Both export services already write `metadata: { kind, row_count }` through
 * `UnifiedExportService`, so this is a read of an existing recorded fact rather
 * than a new field somebody has to remember to set (R-43 — reuse of
 * `GenericAttachment.metadata`, the declared per-owner-type stash).
 *
 * Returns `null` for anything that is not a non-empty string, so a row with no
 * metadata, no `kind`, or a `kind` of the wrong shape all take the one
 * "no recorded kind" path rather than three.
 */
export function recordedExportKind(metadata) {
    const raw = metadata?.kind;
    return typeof raw === 'string' && raw.length > 0 ? raw : null;
}
/**
 * Sprint 62.58.0 PR 3 (step-2 confirmation P3-2) — the export types of a
 * sectioned owner type whose stored file could print a restricted section:
 * the RoPA and activities exports. A file of either written before gate E12.5
 * pass 3 recorded no kind, and which sections it printed cannot be recovered.
 */
export const SECTIONED_EXPORT_TYPES = ['ropa', 'activities'];
/**
 * The kind a retrieval decision is made on for a stored artefact: the kind it
 * records (`recordedExportKind`), except that a sectioned owner type's RoPA or
 * activities export with NO recorded kind (missing, null, `''` or any
 * non-string) reads as its export type with ALL FOUR restricted sections —
 * fail closed, on the read side (step-2 confirmation P3-2, P3-3).
 *
 * The boot migration `62.58.0-data-mapping-export-section-kinds` stamps that
 * same kind on the rows it finds, but it cannot be the guarantee: in a rolling
 * deploy a replica still running the old code can write a kind-less file after
 * a run that stamped nothing recorded completion, and a migration that throws
 * is logged while the server listens anyway. So every retrieval path reads the
 * kind through here, and the enumeration clauses withhold the same rows
 * (`kindlessSectionedExportClause` in the backend guard). The cost of
 * over-closing a file that printed fewer sections is one re-export of live
 * data.
 */
export function storedExportKind(ownerType, metadata) {
    const recorded = recordedExportKind(metadata);
    if (recorded !== null || !SECTIONED_EXPORT_OWNER_TYPES.has(ownerType))
        return recorded;
    // Final confirmation P3-1 — shapes no writer produces but a direct database
    // write could: `metadata` itself a list, or a `kind` stored as a list. The
    // enumeration clauses withhold both (the store's query reads into a list), so
    // the read side fails closed on them too rather than falling back to the
    // owner type's plain rule.
    if (Array.isArray(metadata) || Array.isArray(metadata?.kind)) {
        return sectionedExportKind(SECTIONED_EXPORT_TYPES[0], RESTRICTED_EXPORT_SECTION_ORDER);
    }
    const raw = metadata?.export_type;
    // A list is read as the store's query reads it (any member), so the read
    // side withholds what the enumeration clause withholds.
    const base = (Array.isArray(raw) ? raw : [raw]).find((v) => typeof v === 'string' && SECTIONED_EXPORT_TYPES.includes(v));
    return base === undefined ? null : sectionedExportKind(base, RESTRICTED_EXPORT_SECTION_ORDER);
}
/**
 * Resolve what a caller must hold to retrieve an artefact, from the owner type
 * AND the export kind recorded on it.
 *
 * `exportKind` is **required**, never optional. An optional security parameter is
 * one a caller omits and nothing notices; required makes `tsc` enumerate every
 * call site, which is a better reviewer than a reviewer. Pass `null` explicitly
 * for the many owner types that record no kind — the resolution below says what
 * that means for each.
 *
 * ## What happens to an artefact with no recorded kind
 *
 * Two different things, and the difference is derived rather than decided
 * per type:
 *
 * - The type's kind table resolves to **one distinct grant** (all six logistics
 *   export types — 62.9.0 split them one-per-grant): that grant. This is not a
 *   guess. Every artefact stored under such a type was created through a route
 *   gated on that permission, because there is no other route that can write it,
 *   so serving on that grant is provably neither wider nor narrower than
 *   creation. Refusing here would fail closed against nothing and break
 *   already-shipped, already-correct downloads of rows written before this sprint.
 * - The type's kind table resolves to **more than one grant**
 *   (`manufacturing_export`, six over eight registers — re-measured at Sprint
 *   64.62.0 kickoff; this docblock said "five", which is the count of the
 *   service's *sub-domains*, not of the distinct grants): the T1 fallback, i.e.
 *   refused for everyone but a T1 role. The kind is what the grant depends on, so
 *   an artefact that does not record it does not carry enough information to be
 *   served safely, and the safe direction is the one that cannot leak a substance
 *   register to a batch-records reader. The cost of being wrong here is one
 *   re-export of live data; the cost of the other direction is the defect this
 *   sprint exists for.
 */
export function resolveDownloadGrant(ownerType, exportKind) {
    const kindTable = Object.prototype.hasOwnProperty.call(EXPORT_KIND_DOWNLOAD_GATES, ownerType)
        ? EXPORT_KIND_DOWNLOAD_GATES[ownerType]
        : undefined;
    if (kindTable) {
        if (exportKind === null) {
            const grants = [...new Set(Object.values(kindTable))];
            if (grants.length === 1) {
                return {
                    required: [grants[0]],
                    required_all_of: [],
                    basis: 'sole_grant_default',
                    export_kind: null,
                };
            }
            return {
                required: [OWNER_TYPE_DOWNLOAD_FALLBACK],
                required_all_of: [],
                basis: 'export_kind_missing',
                export_kind: null,
            };
        }
        if (Object.prototype.hasOwnProperty.call(kindTable, exportKind)) {
            return {
                required: [kindTable[exportKind]],
                required_all_of: [],
                basis: 'export_kind',
                export_kind: exportKind,
            };
        }
        return {
            required: [OWNER_TYPE_DOWNLOAD_FALLBACK],
            required_all_of: [],
            basis: 'export_kind_unmapped',
            export_kind: exportKind,
        };
    }
    if (Object.prototype.hasOwnProperty.call(OWNER_TYPE_DOWNLOAD_POLICY, ownerType)) {
        const required = OWNER_TYPE_DOWNLOAD_POLICY[ownerType];
        // Sprint 62.58.0 (gate E12.5 pass 3, P2-1) — the sections the kind names
        // add their grants as an all-of; a kind naming none keeps the row's rule.
        if (SECTIONED_EXPORT_OWNER_TYPES.has(ownerType)) {
            const { declared, undeclared } = restrictedSectionsOfKind(exportKind);
            if (undeclared.length > 0) {
                return {
                    required: [OWNER_TYPE_DOWNLOAD_FALLBACK],
                    required_all_of: [],
                    basis: 'export_section_unmapped',
                    export_kind: exportKind,
                };
            }
            if (declared.length > 0) {
                const allOf = new Set();
                for (const section of declared) {
                    for (const p of RESTRICTED_EXPORT_SECTION_GATES[section])
                        allOf.add(p);
                }
                return {
                    required,
                    required_all_of: [...allOf],
                    basis: 'export_sections',
                    export_kind: exportKind,
                };
            }
            // Gate E12.5 step-2 verification (nit) — a sectioned type's plain kind
            // (`ropa`, `flows`) is recorded on the artefact, so the answer names it
            // and a denial row keeps it; the rule is still the owner type's.
            return { required, required_all_of: [], basis: 'owner_type', export_kind: exportKind };
        }
        return { required, required_all_of: [], basis: 'owner_type', export_kind: null };
    }
    return {
        required: [OWNER_TYPE_DOWNLOAD_FALLBACK],
        required_all_of: [],
        basis: 'owner_type_unmapped',
        export_kind: null,
    };
}
/**
 * Resolve the additional permission requirement for downloading (or
 * exposing metadata of) an attachment.
 *
 * @param exportKind the kind recorded on the artefact (`recordedExportKind`),
 * or `null`. **Required** — see `resolveDownloadGrant`.
 * @returns `null` when the type deliberately has no additional
 * requirement; otherwise a non-empty any-of list. Unknown strings, unmapped
 * kinds and kind-dependent types with no recorded kind get the fail-closed
 * fallback.
 */
export function requiredDownloadPermissions(ownerType, exportKind) {
    return resolveDownloadGrant(ownerType, exportKind).required;
}
/**
 * True when a session holding `permissions` may download / see metadata
 * of an attachment with the given owner type and recorded export kind
 * (any-of semantics).
 */
export function isOwnerTypeDownloadPermitted(permissions, ownerType, exportKind) {
    const { required, required_all_of } = resolveDownloadGrant(ownerType, exportKind);
    const held = new Set(permissions ?? []);
    // Sprint 62.58.0 (gate E12.5 pass 3, P2-1) — the restricted sections' grants
    // are held in full, whatever the owner type's any-of answers.
    if (!required_all_of.every((p) => held.has(p)))
        return false;
    if (required === null)
        return true;
    if (held.size === 0)
        return false;
    return required.some((p) => held.has(p));
}
/**
 * The per-kind retrieval scope of a session, for enumeration filtering.
 *
 * The owner type alone cannot scope a list of `manufacturing_export` rows: five
 * grants share it. Every answer here comes from `isOwnerTypeDownloadPermitted`,
 * the same predicate the download route runs, so a list surface cannot drift
 * from the byte-serving surface it advertises.
 */
export function kindedDownloadScopes(permissions) {
    return KINDED_EXPORT_OWNER_TYPES.map((ownerType) => {
        const permitted_kinds = [];
        const denied_kinds = [];
        for (const kind of Object.keys(EXPORT_KIND_DOWNLOAD_GATES[ownerType])) {
            (isOwnerTypeDownloadPermitted(permissions, ownerType, kind)
                ? permitted_kinds
                : denied_kinds).push(kind);
        }
        return {
            owner_type: ownerType,
            permitted_kinds,
            denied_kinds,
            kindless_permitted: isOwnerTypeDownloadPermitted(permissions, ownerType, null),
        };
    });
}
// ───────────────────────────────────────────────────────────────────────────
// Sprint 63.3.0 — the WRITE analogue of the download policy (idea-catalog #147)
// ───────────────────────────────────────────────────────────────────────────
/**
 * Sprint 64.32.0 shipped `assertOwnerTypeUploadPermitted`, which requires the
 * *download* grant on upload. Its own docblock recorded what that deliberately
 * is not:
 *
 * > *"a role holding `incident:read` still passes for `incident_evidence` … The
 * > write-permission analogue is a per-type decision across 175 owner types and
 * > is a different sprint; it is routed, not silently absorbed."*
 *
 * This is that sprint, and the unit in that sentence was wrong twice. The
 * decision is not per owner **type** (217 of them) — it is per **cited read
 * permission**, of which there are 108, and 76 of those need no decision at all
 * because `<x>:read` has a `<x>:write` sibling in {@link Permission} and the
 * answer is a string substitution. What is left is 18 grants whose write verb is
 * not `:write` ({@link OWNER_TYPE_WRITE_GRANT_OVERRIDES}) and 14 whose module
 * declares no write-shaped permission at all ({@link NO_WRITE_ANALOGUE}).
 *
 * **Verb substitution is not a vocabulary claim (R-46).** It reads two strings
 * out of `Permission` and compares them. It names no module, no instrument and
 * no authority.
 *
 * **A cited permission that is not `:read`-suffixed contributes nothing.** 19 of
 * the 127 cited permissions are already verb-specific grants
 * (`framework_evidence_pack:create`, `incident_export:execute`,
 * `payments_evidence:unmask`, the `:export` and `:download` family). There is no
 * `:read` to substitute on, and manufacturing a write requirement out of a
 * retrieval verb would be this file inventing policy rather than deriving it.
 * The download floor those types already carry is unchanged, so the direction of
 * the omission is the safe one.
 */
const READ_SUFFIX = ':read';
/**
 * The 18 cited `:read` grants whose module declares a write-shaped permission
 * under a verb other than `:write`.
 *
 * **Keyed on the READ permission, not the owner type** — 18 rows rather than
 * 217 hand judgements, and a second owner type citing the same read grant
 * inherits the ruling instead of repeating it.
 *
 * The register is asserted **bidirectionally** by
 * `upload-write-grant-63.3.0.test.ts`: a key naming a permission no policy row
 * cites fails, and a cited `:read` grant with no `:write` sibling, no row here
 * and no row in {@link NO_WRITE_ANALOGUE} fails. That is the property
 * `ACCESS_KEY_PATTERNS` lacked for three sprints.
 */
export const OWNER_TYPE_WRITE_GRANT_OVERRIDES = {
    [Permission.GOV_APPROVAL_READ]: [Permission.GOV_APPROVAL_DECIDE],
    [Permission.CASE_READ]: [Permission.CASE_CREATE, Permission.CASE_UPDATE],
    [Permission.MARKETING_COMMUNICATIONS_READ]: [Permission.MARKETING_COMMUNICATIONS_MANAGE],
    [Permission.FRAMEWORK_EVIDENCE_PACK_READ]: [Permission.FRAMEWORK_EVIDENCE_PACK_CREATE],
    [Permission.PENALTY_EXPOSURE_READ]: [Permission.PENALTY_EXPOSURE_MANAGE],
    [Permission.ENFORCEMENT_CASE_READ]: [Permission.ENFORCEMENT_CASE_MANAGE],
    [Permission.AUTHORITY_DIRECTION_READ]: [Permission.AUTHORITY_DIRECTION_MANAGE],
    [Permission.REGULATED_ACCOUNT_READ]: [Permission.REGULATED_ACCOUNT_MANAGE],
    [Permission.CREDIT_INFORMATION_SUPPLY_READ]: [Permission.CREDIT_INFORMATION_SUPPLY_MANAGE],
    [Permission.DATA_SERVICE_PROVIDER_READ]: [Permission.DATA_SERVICE_PROVIDER_MANAGE],
    // Sprint 63.24.0 Slice B — uploading qualification evidence needs the case's
    // manage grant: the record is changed by the same people who change the case.
    [Permission.CONTROLLED_TESTING_CASE_READ]: [Permission.CONTROLLED_TESTING_CASE_MANAGE],
    // Sprint 62.50.0 — evidence is bound to a publicity record, an information
    // request or the connection-readiness record; uploading it needs the write
    // grant of one of the registers that holds evidence (any-of).
    [Permission.PCF_SUPERVISION_READ]: [
        Permission.PCF_PUBLICITY_WRITE,
        Permission.PCF_INFORMATION_REQUEST_WRITE,
        Permission.PCF_SUPERVISION_SYSTEM_WRITE,
    ],
    // Sprint 62.55.0 — evidence is bound to a dossier, a received document or a
    // payment; uploading it needs the write grant of a register that holds evidence
    // (any-of). The export owner type cites the same read and is written by
    // `UnifiedExportService` under the export grant.
    [Permission.CUSTOMS_TAX_ADMINISTRATION_READ]: [
        Permission.CUSTOMS_TAX_DOSSIER_WRITE,
        Permission.CUSTOMS_TAX_PAYMENT_WRITE,
    ],
    // Sprint 62.56.0 — evidence is bound to a capture component (evidence areas
    // and, round 2, its deletion records), a custody transfer or (round 2) a
    // supervision act; uploading it needs the write grant of one of the
    // registers that hold evidence (any-of), and `owner-record-write-grant.ts`
    // holds it to the owning record's own grant on top.
    [Permission.BIOMETRIC_CAPTURE_READ]: [
        Permission.BIOMETRIC_CAPTURE_COMPONENT_WRITE,
        Permission.BIOMETRIC_CAPTURE_CUSTODY_WRITE,
        Permission.BIOMETRIC_CAPTURE_SUPERVISION_WRITE,
    ],
    [Permission.COMPLIANCE_OBLIGATION_READ]: [Permission.COMPLIANCE_OBLIGATION_MANAGE],
    // Sprint 62.51.0 — evidence is bound to a data-exploitation record, written
    // under the one manage grant of the registers.
    [Permission.DATA_EXPLOITATION_READ]: [Permission.DATA_EXPLOITATION_MANAGE],
    [Permission.EVENT_BUS_READ]: [Permission.EVENT_BUS_MANAGE],
    [Permission.GROUP_READ]: [Permission.GROUP_MANAGE],
    [Permission.GROUP_SHARED_DATA_READ]: [Permission.GROUP_SHARED_DATA_MANAGE],
    [Permission.PARTNER_REPORT_READ]: [Permission.PARTNER_REPORT_GENERATE],
    [Permission.TM_TASK_READ]: [Permission.TM_TASK_CREATE, Permission.TM_TASK_UPDATE],
    [Permission.ACKNOWLEDGMENT_READ]: [Permission.ACKNOWLEDGMENT_MANAGE],
    [Permission.ISSUED_RECORD_READ]: [Permission.ISSUED_RECORD_MANAGE],
    // Prerequisite evidence is read with the title and recorded by its manager.
    [Permission.PUBLICATION_READ]: [Permission.PUBLICATION_MANAGE],
    // Sprint 62.54.0 — a fee-line receipt is uploaded by whoever records the line
    // (`sector_licence:manage`). The decision document, which cites the same read
    // grant, is decided per owner type below: it needs the decision grant, not
    // this one (gate E12.5 pass 1).
    [Permission.SECTOR_LICENCE_READ]: [Permission.SECTOR_LICENCE_MANAGE],
};
/**
 * Sprint 62.54.0 (gate E12.5 pass 1) — owner types whose write grant is NOT the
 * one their cited read grant implies, because two owner types share a read
 * grant and are written by different people. Keyed on the owner type, and
 * consulted before the read-grant derivation.
 *
 * - `licence_fee_decision_document` — the authority's decision document is
 *   uploaded by whoever records the decision (`sector_licence_decision:write`,
 *   the decision route's own grant), never by a holder of
 *   `sector_licence:manage` alone: a fee-line recorder could otherwise place a
 *   file the decision route then binds.
 *
 * Held by `fee-attachment-guards-62.54.0.test.ts`: every key has a non-null
 * download-policy row, and each names permissions that exist.
 */
export const OWNER_TYPE_WRITE_GRANT_BY_OWNER_TYPE = {
    licence_fee_decision_document: [Permission.SECTOR_LICENCE_DECISION_WRITE],
    // Sprint 62.52.0 — the restricted read cited by the evidence owner type is not
    // `:read`-suffixed, so nothing derives from it: the evidence is written by
    // whoever records on the registers.
    cybersecurity_assurance_index_evidence: [Permission.CYBERSECURITY_ASSURANCE_INDEX_WRITE],
};
/**
 * The 14 cited `:read` grants whose module declares **no** write-shaped
 * permission of any verb. There is no write analogue to require, so upload keeps
 * the existing download floor for the types that cite them — declared, per row,
 * rather than silently falling through.
 *
 * `agent-os/scripts/…` verbs scanned: `write`, `create`, `manage`, `update`,
 * `upload`, `decide`, `generate`, `execute`, `admin`.
 */
export const NO_WRITE_ANALOGUE = [
    Permission.GOV_EXECUTION_READ,
    Permission.GOV_CROSSBORDER_READ,
    Permission.PDPL_EVIDENCE_PACK_READ,
    Permission.LEGAL_HOLD_READ,
    Permission.AUDIT_EVENTS_READ,
    Permission.CLASSIFICATION_SUGGESTION_READ,
    Permission.DELETION_PROOF_READ,
    Permission.GROUP_DASHBOARD_READ,
    Permission.GROUP_AUDIT_READ,
    Permission.PARTNER_AUDIT_READ,
    Permission.TM_CALENDAR_READ,
    Permission.CHANGE_LOG_READ,
    Permission.TREND_DASHBOARD_READ,
    Permission.REGULATORY_ARTIFACT_READ,
];
/** Every value of {@link Permission}, for the substitution existence test. */
const PERMISSION_VALUES = new Set(Object.values(Permission));
/** The write permission(s) a single cited download grant implies, if any. */
function writeAnalogueOf(read) {
    if (!read.endsWith(READ_SUFFIX))
        return [];
    const substituted = `${read.slice(0, -READ_SUFFIX.length)}:write`;
    if (PERMISSION_VALUES.has(substituted)) {
        return [substituted];
    }
    const override = OWNER_TYPE_WRITE_GRANT_OVERRIDES[read];
    if (override)
        return override;
    return [];
}
/**
 * The write grant an upload of this owner type must ALSO clear.
 *
 * The requirement is **additive**: `assertOwnerTypeUploadPermitted` keeps the
 * download floor and adds this. The floor is not removed — it is what makes a
 * new owner type inherit a gate by having a policy row at all.
 *
 * Kinded export types resolve through their kind table, exactly as
 * `KINDED_EXPORT_UNION_POLICY` does for the read direction, so the two
 * directions cannot drift.
 */
export function deriveOwnerTypeWriteGrant(ownerType) {
    const kindTable = Object.prototype.hasOwnProperty.call(EXPORT_KIND_DOWNLOAD_GATES, ownerType)
        ? EXPORT_KIND_DOWNLOAD_GATES[ownerType]
        : undefined;
    let cited;
    if (kindTable) {
        cited = [...new Set(Object.values(kindTable))];
    }
    else if (Object.prototype.hasOwnProperty.call(OWNER_TYPE_DOWNLOAD_POLICY, ownerType)) {
        cited = OWNER_TYPE_DOWNLOAD_POLICY[ownerType];
    }
    else {
        return { required: null, basis: 'owner_type_unmapped' };
    }
    if (cited === null)
        return { required: null, basis: 'ungated' };
    const byOwnerType = Object.prototype.hasOwnProperty.call(OWNER_TYPE_WRITE_GRANT_BY_OWNER_TYPE, ownerType)
        ? OWNER_TYPE_WRITE_GRANT_BY_OWNER_TYPE[ownerType]
        : undefined;
    if (byOwnerType && byOwnerType.length > 0) {
        return { required: [...byOwnerType], basis: 'derived' };
    }
    const derived = new Set();
    for (const read of cited)
        for (const w of writeAnalogueOf(read))
            derived.add(w);
    if (derived.size === 0)
        return { required: null, basis: 'no_write_analogue' };
    return { required: [...derived], basis: 'derived' };
}
/**
 * True when a session holding `permissions` clears the derived write grant for
 * `ownerType` (any-of). `null` requirements pass.
 */
export function isOwnerTypeWritePermitted(permissions, ownerType) {
    const { required } = deriveOwnerTypeWriteGrant(ownerType);
    if (required === null)
        return true;
    if (!permissions || permissions.length === 0)
        return false;
    const held = new Set(permissions);
    return required.some((p) => held.has(p));
}
//# sourceMappingURL=attachment-download-policy.js.map